import { Container } from '../ui/Container';
import { Section } from '../ui/Section';
import { FadeUp } from '../ui/FadeUp';
import { cn } from '../../lib/cn';

interface ClientLogo {
  name: string;
  /** estilo visual do wordmark (peso, fonte, tracking) pra simular logo proprio */
  weight?: 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  font?: 'display' | 'heading' | 'mono';
  italic?: boolean;
  uppercase?: boolean;
  letterSpacing?: 'tight' | 'normal' | 'wide';
  /** prefixo decorativo (◆ ▲ ● etc) */
  glyph?: string;
}

const clients: ClientLogo[] = [
  { name: 'Creatorocket', font: 'display', weight: 'extrabold', letterSpacing: 'tight', glyph: '▲' },
  { name: 'Atendly', font: 'heading', weight: 'bold', letterSpacing: 'tight' },
  { name: 'Inteligência Bot', font: 'display', weight: 'medium', italic: true },
  { name: 'Tickpost', font: 'heading', weight: 'bold', uppercase: true, letterSpacing: 'wide', glyph: '●' },
  { name: 'JDS Máquinas', font: 'heading', weight: 'extrabold', letterSpacing: 'tight' },
  { name: 'MRN', font: 'display', weight: 'extrabold', uppercase: true, letterSpacing: 'wide' },
  { name: 'LiberPay', font: 'display', weight: 'semibold', italic: true, letterSpacing: 'tight' },
  { name: 'Loomis', font: 'heading', weight: 'bold', letterSpacing: 'tight', glyph: '◆' },
  { name: 'Commandix', font: 'mono', weight: 'medium', uppercase: true, letterSpacing: 'wide' },
  { name: 'MedMentoria', font: 'display', weight: 'medium', letterSpacing: 'tight' },
  { name: 'Stint', font: 'heading', weight: 'extrabold', uppercase: true, letterSpacing: 'wide', glyph: '✦' },
  { name: 'Nova Legião', font: 'display', weight: 'bold', italic: true },
];

export function LogosMarquee() {
  return (
    <Section spacing="tight" className="bg-bg-base">
      <Container>
        <FadeUp>
          <div className="flex flex-col items-center gap-3 mb-10">
            <span className="text-eyebrow">Empresas que trabalham com a Thux</span>
            <h2 className="text-2xl md:text-3xl text-white text-center heading-h2 max-w-2xl">
              <span className="text-ink-500">Mais de 200 empresas atendidas.</span>{' '}
              Resultado comprovado em empresas reais.
            </h2>
          </div>
        </FadeUp>
      </Container>

      {/* Marquee full-bleed com gradiente nas pontas (fora do container pra ficar largo) */}
      <FadeUp delay={0.1} className="mt-4">
        <div
          className="relative w-full overflow-hidden"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          }}
        >
          <ul className="flex w-max items-center gap-12 md:gap-16 animate-marquee-x py-6">
            {[...clients, ...clients, ...clients].map((c, i) => (
              <li key={`${c.name}-${i}`} className="flex-shrink-0">
                <BrandWordmark logo={c} />
              </li>
            ))}
          </ul>
        </div>
      </FadeUp>
    </Section>
  );
}

const fontClassMap = {
  display: 'font-display',
  heading: 'font-heading',
  mono: 'font-mono',
};

const weightClassMap = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
};

const trackingClassMap = {
  tight: 'tracking-tighter',
  normal: 'tracking-normal',
  wide: 'tracking-[0.18em]',
};

function BrandWordmark({ logo }: { logo: ClientLogo }) {
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-3 whitespace-nowrap select-none',
        'text-2xl md:text-3xl lg:text-4xl text-ink-300/80',
        'transition-all duration-300 hover:text-white hover:scale-[1.05]',
        fontClassMap[logo.font ?? 'display'],
        weightClassMap[logo.weight ?? 'bold'],
        trackingClassMap[logo.letterSpacing ?? 'normal'],
        logo.italic && 'italic',
        logo.uppercase && 'uppercase'
      )}
    >
      {logo.glyph && (
        <span aria-hidden className="text-brand-300/80 text-xl">
          {logo.glyph}
        </span>
      )}
      <span>{logo.name}</span>
    </span>
  );
}
